(() => {
  const selectors = {
    section: '[data-delivery-location-section]',
    component: '[data-delivery-location-selector]',
    selectedText: '[data-delivery-selected-text]',
    modal: '[data-delivery-location-modal]',
    close: '[data-delivery-modal-close]',
    form: '[data-delivery-location-form]',
    input: '[data-delivery-pincode-input]',
    message: '[data-delivery-location-message]',
    enableLocation: '[data-delivery-location-enable]',
    clickpostForm: '.clickpost-edd__form',
    clickpostInput: '.clickpost-edd__drop_pincode-input',
    clickpostButton: '.clickpost-edd__submit-button',
    badge: '[data-quick-delivery-badge]',
    badgePincode: '[data-quick-delivery-pincode]',
    estimateText: '[data-quick-delivery-estimate-text]',
    shortText: '[data-quick-delivery-short-text]',
    sizeIcon: '[data-quick-delivery-size-icon]',
    homepageCta: '[data-quick-delivery-homepage-cta], .homepage-sticky-mini-rush',
    homepageEstimate: '[data-quick-delivery-homepage-estimate]',
    note: '[data-quick-delivery-note]',
    config: '[data-quick-delivery-config]',
    cartLineItem: '[data-cart-line-item]'
  };

  const classes = {
    modalOpen: 'is-open',
    bodyModalOpen: 'delivery-location-modal-open',
    emptySelector: 'delivery-location-selector--empty',
    errorMessage: 'is-error',
    successMessage: 'is-success',
    hidden: 'is-hidden',
    sizeIconVisible: 'is-quick-delivery-visible',
    quickDeliveryEligible: 'is-quick-delivery-eligible' // Added for Cart Sync
  };

  const defaults = {
    storageKey: 'deliveryLocation',
    pendingStorageKey: 'deliveryLocationPendingPincode',
    miniRushApiPath: '/a/mkgen/pincheck',
    invalidMessage: 'Please enter a valid Indian 6-digit pincode.',
    lookupMessage: 'Checking pincode...',
    lookupErrorMessage: 'Please enter a valid Indian pincode.',
    successMessage: 'Delivery available for this location.',
    estimate: '4 hours',
    noteEnabled: true,
    noteTimeRuleEnabled: false,
    noteHideStart: '11:00',
    noteHideEnd: '20:00',
    noteTimezone: 'Asia/Kolkata',
    noteText: '',
  };

  let activeModal = null;
  let lastFocusedElement = null;
  let isInitialized = false;
  let isCheckingPincode = false;
  let activeLookupPincode = '';
  
  let refreshTimer = null;
  let mutationObserver = null;
  let timeRefreshInterval = null;
  let isCartSyncing = false; // Mutex lock for Cart Sync

  const dynamicFeatureSelector = [
    selectors.badge,
    selectors.sizeIcon,
    selectors.homepageCta,
    selectors.note,
    selectors.cartLineItem
  ].join(',');

  // --- Storage & Utils ---

  function safeParseJSON(value) { try { return JSON.parse(value); } catch { return null; } }
  function readStorageJSON(storage, key) { try { return safeParseJSON(storage.getItem(key)); } catch { return null; } }
  function writeStorageJSON(storage, key, value) { try { storage.setItem(key, JSON.stringify(value)); } catch {} }
  function locationsMatch(a, b) { if (!a || !b) return false; try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; } }
  function removeStorageValue(key) { try { window.localStorage.removeItem(key); window.sessionStorage.removeItem(key); } catch {} }

  function getSection(element = null) { return element?.closest?.(selectors.section) || document.querySelector(selectors.section); }
  function getStorageKey(section = null) { return section?.dataset.storageKey || getSection()?.dataset.storageKey || defaults.storageKey; }
  function getMiniRushApiPath(section = null) { return section?.dataset.miniRushApiPath || getSection()?.dataset.miniRushApiPath || defaults.miniRushApiPath; }

  function normalizePincode(value) { return String(value || '').replace(/\D/g, '').slice(0, 6); }
  function validatePincodeFormat(pincode) { return /^[1-9][0-9]{5}$/.test(pincode); }

  function formatDeliveryTime(minutes, fallbackStr) {
    const parsed = parseInt(minutes, 10);
    if (isNaN(parsed) || parsed <= 0) return fallbackStr;

    const hours = Math.floor(parsed / 60);
    const mins = parsed % 60;

    if (hours > 0 && mins === 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
    if (hours > 0 && mins > 0) return `${hours} hr${hours > 1 ? 's' : ''} ${mins} min`;
    return `${mins} min${mins > 1 ? 's' : ''}`;
  }

  // --- Core State Management ---

function getSavedLocation(storageKey = defaults.storageKey) {
    const sessionLoc = readStorageJSON(window.sessionStorage, storageKey);
    const localLoc = readStorageJSON(window.localStorage, storageKey);

    let savedLocation = null;

    if (sessionLoc && localLoc) {
      // Compare timestamps to find the most recent update across tabs
      const sessionTime = new Date(sessionLoc.selectedAt || 0).getTime();
      const localTime = new Date(localLoc.selectedAt || 0).getTime();
      savedLocation = localTime >= sessionTime ? localLoc : sessionLoc;
    } else {
      savedLocation = localLoc || sessionLoc;
    }

    // Hydrate only a missing or stale layer so both stay synced
    if (savedLocation?.pincode) {
      if (!locationsMatch(sessionLoc, savedLocation)) writeStorageJSON(window.sessionStorage, storageKey, savedLocation);
      if (!locationsMatch(localLoc, savedLocation)) writeStorageJSON(window.localStorage, storageKey, savedLocation);
    }
    
    return savedLocation;
  }

  function hasPendingPincode() {
    try { 
      return Boolean(window.sessionStorage.getItem(defaults.pendingStorageKey) || window.localStorage.getItem(defaults.pendingStorageKey)); 
    } catch { return false; }
  }

  function setPendingPincode(pincode) {
    const normalizedPincode = String(pincode || '').trim();
    if (normalizedPincode) {
      try { 
        window.sessionStorage.setItem(defaults.pendingStorageKey, normalizedPincode); 
        window.localStorage.setItem(defaults.pendingStorageKey, normalizedPincode); 
      } catch {}
    } else removeStorageValue(defaults.pendingStorageKey);

    const detail = { pincode: normalizedPincode };
    document.dispatchEvent(new CustomEvent('delivery-location:pending', { bubbles: true, detail }));
    document.dispatchEvent(new CustomEvent('delivery-location:pending-state', { bubbles: true, detail }));
  }

  function dispatchDeliveryUpdate(locationData) {
    document.dispatchEvent(new CustomEvent('delivery-location:updated', { bubbles: true, detail: locationData }));
    document.dispatchEvent(new CustomEvent('delivery-location:select', { bubbles: true, detail: locationData }));
  }

  function normalizeLocationData(locationData) {
    const deliveryEstimate = locationData.deliveryEstimate || locationData.estimate || defaults.estimate;
    return {
      pincode: String(locationData.pincode || '').trim(),
      location: String(locationData.location || '').trim(),
      normalDeliveryAvailable: locationData.normalDeliveryAvailable !== false,
      quickDeliveryServiceable: locationData.quickDeliveryServiceable === true,
      warehouseCode: String(locationData.warehouseCode || '').trim(),
      warehouseId: String(locationData.warehouseId || locationData.warehouseCode || '').trim(),
      warehouseName: String(locationData.warehouseName || '').trim(),
      warehousePincode: String(locationData.warehousePincode || '').trim(),
      apiServiceable: locationData.apiServiceable === true,
      estimate: deliveryEstimate,
      deliveryEstimate,
      source: locationData.source || 'delivery-location-selector',
      selectedAt: new Date().toISOString(),
    };
  }

  function saveLocation(section, locationData) {
    const storageKey = getStorageKey(section);
    const savedLocation = normalizeLocationData(locationData);
    
    writeStorageJSON(window.localStorage, storageKey, savedLocation);
    writeStorageJSON(window.sessionStorage, storageKey, savedLocation);
    
    setPendingPincode('');
    dispatchDeliveryUpdate(savedLocation);
    return savedLocation;
  }

  function clearSavedLocation() {
    removeStorageValue(getStorageKey());
    setPendingPincode('');
    dispatchDeliveryUpdate({
      pincode: '', location: '', normalDeliveryAvailable: false, quickDeliveryServiceable: false,
      warehouseCode: '', warehouseId: '', warehouseName: '', warehousePincode: '',
      apiServiceable: false, estimate: '', deliveryEstimate: '', source: 'pdp-invalid-pincode',
    });
  }

  // --- API Lookups ---

  async function lookupIndianPincode(pincode, useTimeout = false) {
    const fallbackResult = { pincode, location: `Pincode ${pincode}`, postOfficeName: '', district: '', state: '', deliveryStatus: '' };
    const controller = new AbortController();
    const timeoutId = useTimeout ? setTimeout(() => controller.abort(), 1500) : null;

    try {
      const response = await fetch(`https://api.postalpincode.in/pincode/${encodeURIComponent(pincode)}`, {
        method: 'GET', headers: { Accept: 'application/json' }, ...(useTimeout && { signal: controller.signal })
      });
      if (timeoutId) clearTimeout(timeoutId);
      if (!response.ok) return useTimeout ? fallbackResult : null;

      const data = await response.json();
      const result = Array.isArray(data) ? data[0] : null;
      if (String(result?.Status || '').toLowerCase() !== 'success' || !result?.PostOffice?.length) return useTimeout ? fallbackResult : null;

      const po = result.PostOffice.find(p => String(p?.DeliveryStatus || '').toLowerCase() === 'delivery') || result.PostOffice[0];
      if (!po) return useTimeout ? fallbackResult : null;

      return {
        pincode, location: [po.Name, po.District, po.State].filter(Boolean).join(', '),
        postOfficeName: po.Name || '', district: po.District || '', state: po.State || '', deliveryStatus: po.DeliveryStatus || '',
      };
    } catch (error) {
      if (timeoutId) clearTimeout(timeoutId);
      return useTimeout ? fallbackResult : null;
    }
  }

  function calculateDeliveryDate(daysToAdd) {
    const date = new Date();
    let addedDays = 0;
    const parsedDays = parseInt(daysToAdd, 10);
    
    if (isNaN(parsedDays) || parsedDays <= 0) return 'soon';

    while (addedDays < parsedDays) {
      date.setDate(date.getDate() + 1);
      // Skip Sundays (0)
      if (date.getDay() !== 0) { 
        addedDays++;
      }
    }
    
    // Formats as "24 Oct" or "Oct 24" based on locale
    return new Intl.DateTimeFormat('en-IN', { month: 'short', day: 'numeric' }).format(date);
  }

function normalizeMiniRushResponse(apiData) {
    const data = apiData?.data || apiData?.result || apiData || {};

    const isServiceable = data.serviceable === true;
    const isQuickAvailable = data.quick_available === true;
    const warehousePincode = String(data.pickupLocation || '').trim();

    let finalEstimate = defaults.estimate;

    // Estimate formatting priority: Quick Delivery (mins) -> Standard Delivery (Dates)
    if (isQuickAvailable && data.deliverTime) {
      finalEstimate = formatDeliveryTime(data.deliverTime, finalEstimate);
    } else if (isServiceable && data.sla_min !== null && data.sla_max !== null) {
      const minDate = calculateDeliveryDate(data.sla_min);
      const maxDate = calculateDeliveryDate(data.sla_max);
      
      finalEstimate = data.sla_min === data.sla_max 
        ? `by ${minDate}` 
        : `between ${minDate} and ${maxDate}`;
    }

    return {
      normalDeliveryAvailable: isServiceable,
      quickDeliveryServiceable: isQuickAvailable && warehousePincode !== '',
      apiServiceable: isServiceable || isQuickAvailable, 
      warehousePincode: warehousePincode,
      warehouseCode: warehousePincode,
      estimate: finalEstimate,
    };
  }

  async function lookupMiniRushAvailability(section, pincode) {
    const apiPath = getMiniRushApiPath(section);
    try {
      const response = await fetch(`${apiPath}${apiPath.includes('?') ? '&' : '?'}pincode=${encodeURIComponent(pincode)}`, { method: 'GET', headers: { Accept: 'application/json' } });
      if (!response.ok) return normalizeMiniRushResponse({ available: false });
      return normalizeMiniRushResponse(await response.json());
    } catch {
      return normalizeMiniRushResponse({ available: false });
    }
  }

  // --- Time & Note Logic ---

  function getMasterConfig() {
    const configEl = document.querySelector(selectors.config);
    if (!configEl) return { ...defaults };
    return {
      noteEnabled: configEl.dataset.pdpNoteEnabled !== 'false',
      noteTimeRuleEnabled: configEl.dataset.pdpNoteTimeRuleEnabled === 'true',
      noteHideStart: configEl.dataset.pdpNoteHideStart || defaults.noteHideStart,
      noteHideEnd: configEl.dataset.pdpNoteHideEnd || defaults.noteHideEnd,
      noteTimezone: configEl.dataset.pdpNoteTimezone || defaults.noteTimezone,
      noteText: configEl.dataset.pdpNoteText || defaults.noteText,
    };
  }

  function parseTimeToMinutes(timeValue) {
    const match = String(timeValue || '').trim().match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i);
    if (!match) return null;
    let [ , hours, minutes, meridiem ] = match;
    hours = Number(hours); minutes = Number(minutes);
    if (isNaN(hours) || isNaN(minutes) || hours < 0 || hours > 24 || minutes < 0 || minutes > 59) return null;
    if (meridiem === 'AM' && hours === 12) hours = 0;
    if (meridiem === 'PM' && hours < 12) hours += 12;
    return (hours === 24 ? 0 : hours) * 60 + minutes;
  }

  function shouldShowPdpNoteByTime() {
    const config = getMasterConfig();
    if (!config.noteTimeRuleEnabled) return true;

    const startMinutes = parseTimeToMinutes(config.noteHideStart);
    const endMinutes = parseTimeToMinutes(config.noteHideEnd);
    if (startMinutes === null || endMinutes === null || startMinutes === endMinutes) return false;

    try {
      const parts = new Intl.DateTimeFormat('en-GB', { timeZone: config.noteTimezone, hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(new Date());
      const currentMinutes = (Number(parts.find(p => p.type === 'hour')?.value) % 24) * 60 + Number(parts.find(p => p.type === 'minute')?.value);
      if (isNaN(currentMinutes)) return false;

      const isInsideWindow = startMinutes < endMinutes 
        ? (currentMinutes >= startMinutes && currentMinutes <= endMinutes)
        : (currentMinutes >= startMinutes || currentMinutes <= endMinutes);
      
      return !isInsideWindow;
    } catch { return false; }
  }

  // --- UI Update Pipelines ---

  function isProductQuickDeliveryEnabled(el) {
    if (!el) return false;
    const prodEnabled = el.dataset.quickDeliveryProductEnabled;
    const legacyEnabled = el.dataset.quickDeliveryEnabled;
    return !(prodEnabled === 'false' || legacyEnabled === 'false');
  }

  function syncPdpInput(locationData) {
    const input = document.querySelector(selectors.clickpostInput);
    if (!input || !locationData?.pincode || document.activeElement === input) return;
    [0, 300, 900, 1800].forEach((delay) => {
      window.setTimeout(() => { if (input.value !== locationData.pincode && document.activeElement !== input) input.value = locationData.pincode; }, delay);
    });
  }

  function updateSelectors(locationData) {
    const data = locationData || {}; 

    document.querySelectorAll(selectors.component).forEach((selector) => {
      selector.dataset.pincode = data.pincode || '';
      selector.dataset.location = data.location || '';
      selector.dataset.hasLocation = data.pincode ? 'true' : 'false';

      if (!data.pincode) {
        selector.classList.add(classes.emptySelector);
        selector.setAttribute('aria-label', 'Change delivery location.');
        return; 
      }

      selector.classList.remove(classes.emptySelector);
      selector.setAttribute('aria-label', `Change delivery location. Current location is ${data.pincode}`);
      const selectedText = selector.querySelector(selectors.selectedText);
      if (selectedText) {
        selectedText.innerHTML = `<span class="delivery-location-selector__label">Deliver to</span><span class="delivery-location-selector__separator" aria-hidden="true">:</span><span class="delivery-location-selector__pincode">${data.pincode}</span>${data.location ? `<span class="delivery-location-selector__location">${data.location}</span>` : ''}`;
      }
    });
  }

  // Applies variant-specific stock checks to cart items
  function applyCartQuickDelivery(locationData) {
    const warehouseCode = locationData?.warehousePincode;
    const cartItems = document.querySelectorAll(selectors.cartLineItem);

    cartItems.forEach(item => {
      const locationsStr = item.dataset.quickLocations;
      if (!locationsStr || locationsStr === 'null') return;

      let locations = [];
      try { 
        locations = JSON.parse(locationsStr); 
      } catch (e) {
        console.warn('Failed to parse quick delivery locations', e);
      }

      // Check variant stock eligibility against current warehouse
      const isEligible = Boolean(warehouseCode && locations.includes(warehouseCode));
      
      // Tag line item for backend sync
      item.classList.toggle(classes.quickDeliveryEligible, isEligible);

      // Force UI Badge state
      const badge = item.querySelector(selectors.badge);
      if (badge) {
        badge.classList.toggle(classes.hidden, !isEligible);
        badge.setAttribute('aria-hidden', !isEligible ? 'true' : 'false');
      }
    });
  }

function checkPdpVariantEligibility(locationData) {
    if (!window.quickDeliveryVariants || !locationData?.warehousePincode) return null;

    // 1. Try URL first. Themes update ?variant=ID instantly during AJAX, making it the most reliable.
    const urlParams = new URLSearchParams(window.location.search);
    let variantId = urlParams.get('variant');

    // 2. Fallback to DOM input if no URL param exists
    if (!variantId) {
      const variantInput = document.querySelector('form[action*="/cart/add"] [name="id"], input[name="id"][type="hidden"], select[name="id"]');
      if (variantInput) variantId = variantInput.value;
    }

    if (!variantId) return null;

    const validLocations = window.quickDeliveryVariants[variantId] || [];
    return validLocations.includes(locationData.warehousePincode);
  }

function refreshBadges(rootNode = document, locationData = getSavedLocation()) {
    const root = (rootNode && typeof rootNode.querySelectorAll === 'function') ? rootNode : document;

    const isServiceableGlobal = locationData?.quickDeliveryServiceable === true && !hasPendingPincode();
    const config = getMasterConfig();
    const noteVisibleTime = shouldShowPdpNoteByTime();
    
    // Evaluate the currently selected variant on the PDP
    const isPdpVariantEligible = checkPdpVariantEligibility(locationData);
    
    // 1. Standard Badges
    root.querySelectorAll(selectors.badge).forEach(badge => {
      // Determine if this is the PDP badge or a Cart badge
      const isCartBadge = badge.closest(selectors.cartLineItem) !== null;

      const isServiceableLocal = locationData?.quickDeliveryServiceable === true && !hasPendingPincode() && (getStorageKey() === (badge.dataset.quickDeliveryStorageKey || defaults.storageKey));
      let badgeVisible = isProductQuickDeliveryEnabled(badge) && isServiceableLocal;

      // Apply PDP Variant Override (only affects the main product badge, not cart items)
      if (!isCartBadge && isPdpVariantEligible !== null) {
        badgeVisible = badgeVisible && isPdpVariantEligible;
      }
      
      badge.classList.toggle(classes.hidden, !badgeVisible);
      badge.setAttribute('aria-hidden', badgeVisible ? 'false' : 'true');

      // Notes tied to this badge
      const note = badge.nextElementSibling?.matches?.(selectors.note) ? badge.nextElementSibling : null;
      if (note) {
        const noteVisible = config.noteEnabled && badgeVisible && config.noteText && noteVisibleTime;
        note.textContent = config.noteText;
        note.classList.toggle(classes.hidden, !noteVisible);
        note.setAttribute('aria-hidden', noteVisible ? 'false' : 'true');
      }

      if (badgeVisible && locationData) {
        const est = locationData.deliveryEstimate || locationData.estimate || badge.dataset.quickDeliveryEstimate || defaults.estimate;
        badge.querySelectorAll(selectors.badgePincode).forEach(el => el.textContent = locationData.pincode);
        badge.querySelectorAll(selectors.estimateText).forEach(el => el.textContent = est);
        badge.querySelectorAll(selectors.shortText).forEach(el => el.textContent = est);
      }
    });

    // 2. Size Icons
    root.querySelectorAll(selectors.sizeIcon).forEach(icon => {
      const iconVisible = isProductQuickDeliveryEnabled(icon) && isServiceableGlobal;
      icon.classList.toggle(classes.sizeIconVisible, iconVisible);
      icon.setAttribute('aria-hidden', iconVisible ? 'false' : 'true');
    });

    // 3. Homepage CTAs
    root.querySelectorAll(selectors.homepageCta).forEach(cta => {
      const whPincode = locationData?.warehousePincode;
      const ctaVisible = isServiceableGlobal && whPincode;
      const isSticky = cta.classList.contains('homepage-sticky-mini-rush');
      const baseUrl = new URL(cta.href, window.location.origin);

      if (ctaVisible) {
        baseUrl.searchParams.set('filter.p.m.custom.quick_wh', whPincode);
        const est = locationData.deliveryEstimate || locationData.estimate || defaults.estimate;
        cta.querySelectorAll(selectors.homepageEstimate).forEach(el => el.textContent = est);
      } else {
        baseUrl.searchParams.delete('filter.p.m.custom.quick_wh');
      }
      
      cta.setAttribute('href', baseUrl.toString());

      if (!isSticky || ctaVisible) {
        cta.classList.toggle(classes.hidden, !ctaVisible);
        cta.setAttribute('aria-hidden', ctaVisible ? 'false' : 'true');
        cta.style.display = ctaVisible ? '' : 'none';
      }
    });

    // 4. Overwrite Badges for Cart & Drawer specifically
    applyCartQuickDelivery(locationData);
  }

  function scheduleRefreshBadges(rootNode = document, locationData = null) {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => refreshBadges(rootNode, locationData ?? getSavedLocation()), 150);
  }



  // --- Modals & Interaction ---

function setMessage(el, msg, type) {
    if (!el) return;
    el.innerHTML = msg || '';
    el.classList.remove(classes.errorMessage, classes.successMessage);
    if (type) el.classList.add(type === 'error' ? classes.errorMessage : classes.successMessage);
  }

  function openModal(section = null) {
    const modal = section?.querySelector(selectors.modal) || document.querySelector(selectors.modal);
    if (!modal) return;
    lastFocusedElement = document.activeElement;
    activeModal = modal;
    modal.classList.add(classes.modalOpen);
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add(classes.bodyModalOpen);
    setMessage(modal.querySelector(selectors.message), '', '');
    window.setTimeout(() => modal.querySelector(selectors.input)?.focus(), 80);
  }

  function closeModal(modal) {
    if (!modal) return;
    modal.classList.remove(classes.modalOpen);
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove(classes.bodyModalOpen);
    activeModal = null;
    if (lastFocusedElement?.focus) lastFocusedElement.focus();
  }

 async function handleFormSubmit(event) {
    const form = event.target.closest(selectors.form);
    if (!form || isCheckingPincode) return;
    event.preventDefault();

    const section = getSection(form);
    const input = form.querySelector(selectors.input);
    const message = form.querySelector(selectors.message);
    const submitBtn = form.querySelector('button[type="submit"]');

    if (!input) return;
    const pincode = input.value.trim();
    setPendingPincode(pincode);

    if (!validatePincodeFormat(pincode)) {
      setMessage(message, section?.dataset.invalidMessage || defaults.invalidMessage, 'error');
      return input.focus();
    }

    isCheckingPincode = true;
    if (submitBtn) { submitBtn.disabled = true; submitBtn.setAttribute('aria-busy', 'true'); }
    setMessage(message, defaults.lookupMessage, '');

    const verifiedPincode = await lookupIndianPincode(pincode, true);
    if (!verifiedPincode) {
      isCheckingPincode = false;
      if (submitBtn) { submitBtn.disabled = false; submitBtn.removeAttribute('aria-busy'); }
      setPendingPincode('');
      setMessage(message, section?.dataset.notServiceableMessage || defaults.lookupErrorMessage, 'error');
      return input.focus();
    }

    const miniRushResult = await lookupMiniRushAvailability(section, pincode);
    isCheckingPincode = false;
    if (submitBtn) { submitBtn.disabled = false; submitBtn.removeAttribute('aria-busy'); }
    timeoutVal = 1500;
// State 1: Completely unserviceable
    if (!miniRushResult.apiServiceable) {
      saveLocation(section, { pincode, location: verifiedPincode.location, source: 'mini-rush-api', ...miniRushResult });
      const failMsg = section?.dataset.unserviceableMessage || 'Delivery is not available for this location.';
      setMessage(message, failMsg, 'error');
      return input.focus();
    }

    // Helper to wrap text in a styling hook
    const highlight = (text) => `<span class="delivery-time-highlight">${text}</span>`;

    // State 2: Quick Delivery & Standard Delivery
    if (miniRushResult.quickDeliveryServiceable) {
      // Force standard delivery SLA to 1-2 days
      const minDate = calculateDeliveryDate(1); // Tomorrow
      const maxDate = calculateDeliveryDate(2); // Day after tomorrow
      
      const quickMsg = `${highlight(miniRushResult.estimate)} delivery available for selected products<br>Standard delivery available ${highlight(`between ${minDate} and ${maxDate}`)}.<br>COD and Prepaid Available`;
      timeoutVal = 2000;
      setMessage(message, quickMsg, 'success');
    } 
    // State 3: Standard Delivery Only
    else if (miniRushResult.normalDeliveryAvailable) {
      // In this state, miniRushResult.estimate contains the standard SLA dates from the API
      let stdMsg = section?.dataset.standardSuccessMessage || 'Standard delivery available {estimate}.';
      stdMsg = stdMsg.replace('{estimate}', highlight(miniRushResult.estimate));
      
      const normalMsg = `${stdMsg}<br>COD and Prepaid Available`;
      timeoutVal = 3000;
      setMessage(message, normalMsg, 'success');
    }

    saveLocation(section, { pincode, location: verifiedPincode.location, source: 'mini-rush-api', ...miniRushResult });
    window.setTimeout(() => closeModal(section?.querySelector(selectors.modal) || document.querySelector(selectors.modal)), timeoutVal);
  }

async function commitPdpPincode(pincode) {
    if (!validatePincodeFormat(pincode)) return setPendingPincode('');
    const prevLocation = getSavedLocation();
    activeLookupPincode = pincode;
    setPendingPincode(pincode);

    const verifiedPincode = await lookupIndianPincode(pincode, false);
    if (activeLookupPincode !== pincode) return;

    if (!verifiedPincode) {
      if (prevLocation?.pincode && prevLocation.pincode !== pincode) saveLocation(null, prevLocation);
      else clearSavedLocation();
      return;
    }

    const miniRushResult = await lookupMiniRushAvailability(null, pincode);
    if (activeLookupPincode !== pincode) return;
    
    saveLocation(null, { pincode, location: verifiedPincode.location, source: 'pdp-delivery-details', ...miniRushResult });
  }

  // --- Observers & Setup ---

  function nodeContainsDynamicFeature(node) {
    if (!(node instanceof Element)) return false;
    return node.matches(dynamicFeatureSelector) || Boolean(node.querySelector(dynamicFeatureSelector));
  }

  function getDynamicObserverRoots() {
    const roots = [
      document.getElementById('MainContent'),
      document.getElementById('CartDrawer'),
      ...document.querySelectorAll('quick-view-modal')
    ].filter(Boolean);

    return [...new Set(roots)];
  }

  function observeDynamicContent() {
    const roots = getDynamicObserverRoots();
    if (!roots.length) return;

    if (!mutationObserver) {
      mutationObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (nodeContainsDynamicFeature(node)) {
              scheduleRefreshBadges();
              return;
            }
          }
        }
      });
    } else {
      mutationObserver.disconnect();
    }

    roots.forEach((root) => mutationObserver.observe(root, { childList: true, subtree: true }));
  }

  function startTimeRefreshIfNeeded() {
    if (timeRefreshInterval) return;

    const config = getMasterConfig();
    if (!config.noteTimeRuleEnabled || !document.querySelector(selectors.note)) return;

    timeRefreshInterval = window.setInterval(() => scheduleRefreshBadges(), 60000);
  }

  function initDeliveryLocationSystem() {
    if (isInitialized) return;
    isInitialized = true;

    const savedLocation = getSavedLocation();
    if (savedLocation?.pincode) {
      setPendingPincode('');
      updateSelectors(savedLocation);
      syncPdpInput(savedLocation);
      refreshBadges(document, savedLocation);
    }

    observeDynamicContent();
    startTimeRefreshIfNeeded();

    // Event Listeners: Selectors & Modals
    document.querySelectorAll(selectors.component).forEach(sel => {
      if (sel.dataset.deliveryLocationBound) return;
      sel.dataset.deliveryLocationBound = 'true';
      sel.addEventListener('click', (e) => { e.preventDefault(); openModal(getSection(sel)); });
    });

    document.addEventListener('submit', handleFormSubmit);
    document.addEventListener('keydown', (e) => e.key === 'Escape' && activeModal && closeModal(activeModal));

    const hasPdpDeliveryContext = document.body.classList.contains('product-template')
      || Boolean(document.querySelector(`${selectors.clickpostForm}, ${selectors.clickpostInput}, ${selectors.clickpostButton}`));

    document.addEventListener('input', (e) => {
      const input = e.target.closest(selectors.input);
      if (input) input.value = normalizePincode(input.value);

      if (hasPdpDeliveryContext && e.target.closest(selectors.clickpostInput)) {
        setPendingPincode(normalizePincode(e.target.value));
      }
    });
    
    document.addEventListener('click', (e) => {
      const closeTrigger = e.target.closest(selectors.close);
      if (closeTrigger) closeModal(closeTrigger.closest(selectors.modal) || document.querySelector(selectors.modal));
      const enableBtn = e.target.closest(selectors.enableLocation);
      if (enableBtn) {
        const msg = getSection(enableBtn)?.querySelector(selectors.message) || document.querySelector(selectors.message);
        if (!navigator.geolocation) return setMessage(msg, 'Location access is not supported.', 'error');
        navigator.geolocation.getCurrentPosition(
          () => setMessage(msg, 'Location enabled. Enter pincode.', 'success'),
          () => setMessage(msg, 'Location denied. Enter manually.', 'error'),
          { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
        );
      }
    });

    // Event Listeners: PDP Form Sync
    if (hasPdpDeliveryContext) {
      const handlePdpSubmit = (e) => {
        const form = e.target.closest(selectors.clickpostForm) || document;
        const input = form.querySelector(selectors.clickpostInput);
        if (input) commitPdpPincode(normalizePincode(input.value));
      };

      document.addEventListener('click', (e) => e.target.closest(selectors.clickpostButton) && handlePdpSubmit(e), true);
      document.addEventListener('submit', (e) => e.target.closest(selectors.clickpostForm) && handlePdpSubmit(e), true);
    }

    // Event Listeners: Global State & Environment
    const handleGlobalUpdate = (e) => {
      const loc = (e && e.detail) ? e.detail : (getSavedLocation() || {});
      updateSelectors(loc);
      syncPdpInput(loc);
      scheduleRefreshBadges(document, loc); 
    };

    document.addEventListener('delivery-location:updated', handleGlobalUpdate);
    document.addEventListener('delivery-location:pending', () => scheduleRefreshBadges());
    
   // Listen for updates from other tabs
    window.addEventListener('storage', (e) => {
      // Only react to localStorage changes from other tabs
      if (e.key === defaults.storageKey && e.newValue) {
        try {
          const freshData = JSON.parse(e.newValue);
          if (freshData && freshData.pincode) {
            // Force this tab's sessionStorage to match the new tab's data immediately
            writeStorageJSON(window.sessionStorage, defaults.storageKey, freshData);
            handleGlobalUpdate({ detail: freshData });
          }
        } catch (err) {
          console.error('[Mini Rush] Cross-tab sync error', err);
        }
      }
    });

    // Third-party / Theme Events
    ['quick-view:open', 'quick-view:loaded', 'quickview:open', 'modal:open', 'drawer:open', 'cart:updated', 'cart:refresh', 'cart-drawer:open', 'cart:open', 'variant:change', 'product:variant-change'].forEach(evt => {
      document.addEventListener(evt, () => scheduleRefreshBadges());
    });

    const reloadState = () => handleGlobalUpdate({ detail: getSavedLocation() });
    window.addEventListener('pageshow', reloadState);
    document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && reloadState());
    document.addEventListener('shopify:section:load', () => {
      observeDynamicContent();
      reloadState();
    });
    document.addEventListener('shopify:section:select', reloadState);
    document.addEventListener('shopify:block:select', reloadState);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initDeliveryLocationSystem);
  else initDeliveryLocationSystem();
})();